import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import { DataGrid, GridColDef, ModalOpenButton } from "@msys/ui";
import { Button, Link } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { DebugAllPropsButton } from "../../features/doc-items/debug/PropertiesDebug.js";
import { AttributeExpressionModal } from "../../features/doc-items/modals/AttributeExpressionModal.js";
import { ItemProductSearchFilterExpressionModal } from "../../features/doc-items/modals/ItemProductSearchFilterExpressionModal.js";
import { EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE } from "../../features/doc-items/expressionWriteAccess.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import { PropertyExpressionModal } from "../../features/doc-items/modals/PropertyExpressionModal.js";
import { QuoteSubHeader } from "../../features/quotes/QuoteSubHeader.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { PropertyMappingExpressionModal } from "../../features/templates/quote/PropertyMappingExpressionModal.js";
import {
  ProjectQuoteTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment,
  useProjectQuoteTroubleshootQuery,
} from "./ProjectQuoteTroubleshoot.generated.js";

interface Props {
  projectId: string;
  quoteId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
  pathToDocList: string;
  pathToDoc: string;
}

export function ProjectQuoteTroubleshoot({
  projectId,
  quoteId,
  submenuItems,
  prefixBreadcrumbs,
  activeSubmenuItem,
  pathToDocList,
  pathToDoc,
}: Props) {
  const pathToDocPage = `${pathToDoc}/troubleshoot`;

  const { t } = useTranslate(["Global", "Expressions"]);

  const client = useApolloClient();
  const query = useProjectQuoteTroubleshootQuery({
    client,
    variables: { projectId, docId: quoteId },
    notifyOnNetworkStatusChange: true,
  });
  const project = getDataOrNull(query.data?.project)?.project;
  const quote = getDataOrNull(query.data?.quote)?.quote;
  const expressionValidationResults = getDataOrNull(
    query.data?.compileDocIsolatedExpression
  )?.results;

  const columns: GridColDef<ProjectQuoteTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment>[] =
    [
      {
        field: "title",
        headerName: t("Item", {
          ns: "Global",
        }),
        flex: 1,
        renderCell: ({ row }) => (
          <Link
            href={`${pathToDocList}/${row.docId}/edit/items/${row.itemId}`}
            target="_blank"
            onClick={event => {
              event.stopPropagation();
            }}
          >{`${row.itemPdfPath} ${row.itemTitle}`}</Link>
        ),
      },
      {
        field: "kind",
        headerName: t("Kind", {
          ns: "Expressions",
        }),
        flex: 2,
        valueGetter: (value, row) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return t("Property", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return t("Property", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return t("Attribute", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return t("Property mapping", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return t("Item recommendation eligible if", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return t("Item recommendation include if", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return t("Product search filter expression", {
              ns: "Expressions",
            });
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "location",
        headerName: t("Location", {
          ns: "Expressions",
        }),
        flex: 1,
        valueGetter: (value, row) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return row.key;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return row.attribute;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return row.toKey;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return row.itemRecommendationRank;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return row.itemRecommendationRank;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return row.propertyKey;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return row.key;
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "expression",
        headerName: t("Formula", {
          ns: "Global",
        }),
        flex: 2,
        renderCell: ({ row }) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return (
              <ModalOpenButton
                Modal={PropertyExpressionModal}
                modalProps={{
                  projectId,
                  docId: row.docId,
                  itemId: row.itemId,
                  fieldName: row.key,
                  expression: row.expr,
                }}
              >
                <Link>{row.expr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return (
              <ModalOpenButton
                Modal={AttributeExpressionModal}
                modalProps={{
                  projectId,
                  docId: row.docId,
                  itemId: row.itemId,
                  fieldName: row.attribute,
                  expression: row.expr,
                }}
              >
                <Link>{row.expr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return (
              <ModalOpenButton
                Modal={PropertyMappingExpressionModal}
                modalProps={{
                  projectId,
                  docId: row.docId,
                  itemId: row.itemId,
                  fieldName: row.toKey,
                  expression: row.fromExpr,
                }}
              >
                <Link>{row.fromExpr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            // The save is gated on expression access, so a viewer without it
            // reads the formula rather than opening an editor that only fails.
            return (
              <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                capability={["pim", "expressions"]}
                permissionByType={EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE}
                document={{
                  docType: "QUOTE",
                  viewerPermissions: quote?.viewerPermissions ?? [],
                }}
                otherwise={row.expr}
              >
                <ModalOpenButton
                  Modal={ItemProductSearchFilterExpressionModal}
                  modalProps={{
                    projectId,
                    docId: row.docId,
                    itemId: row.itemId,
                    filterLabel: row.filterKey,
                    filterKind: row.filterKind,
                    propertyKey: row.propertyKey ?? undefined,
                    expression: row.expr,
                    operator: row.operator,
                  }}
                >
                  <Link>{row.expr}</Link>
                </ModalOpenButton>
              </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return null;
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "messageText",
        headerName: t("Error", {
          ns: "Global",
        }),
        flex: 3,
        valueGetter: (value, row) => row.messageText,
      },
    ];

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  return (
    <Page
      title={quote?.title ?? ""}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      header={
        <PageHeader
          breadcrumbs={prefixBreadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      subHeader={
        project && quote ? (
          <QuoteSubHeader
            project={project}
            quote={quote}
            activeView={null}
            canEdit={false}
            isReadOnly={true}
            pathToDocList={pathToDocList}
            pathToDoc={pathToDoc}
            hasSapS4HanaIntegration={hasSapS4HanaIntegration}
            isHeaderVisible={undefined as never}
            setHeaderVisible={undefined as never}
            expandedItemIds={undefined}
          />
        ) : undefined
      }
      action={
        project &&
        quote && [
          <DebugAllPropsButton
            key={"debug-all-props"}
            projectId={projectId}
            docId={quoteId}
          />,
          <Button
            key={"rerun-checks"}
            onClick={() => {
              query.refetch();
            }}
            variant="contained"
            loading={query.loading}
          >
            {t("Re-run checks", {
              ns: "Global",
            })}
          </Button>,
        ]
      }
    >
      <PageContainer>
        <DataGrid<ProjectQuoteTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment>
          columns={columns}
          rows={expressionValidationResults ?? []}
          getRowId={row =>
            `${row.itemId}-${row.__typename}-${JSON.stringify(row)}`
          }
          paginationMode="client"
          loading={query.loading}
          isRowSelectable={() => false}
        />
      </PageContainer>
    </Page>
  );
}
